import { Box } from '@mui/material';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import { waitForWindowToClose } from '../../pages/helper';
import RestrictedTooltip from '../../common/permissions/RestrictedTooltip';
import { ButtonVariants } from '@sweep-io/sweep-design/dist/components/Button/types';
import { retry } from 'ts-retry-promise';
import { useCallback, useState } from 'react';
import { usePermissions } from '../../../permissions/usePermissions';
import { useHubspot } from '../../pages/canvas-pages/canvas-hubspot-page/useHubspot';
import { useHubspotApiFacade } from '../../../apis/facades/useHubspotApiFacade';
import { useFetchGlobal } from '../../../hooks/global-reducer/useFetchGlobal';
import { selectProductionCrmOrg } from '../../pages/environments/environmentsReducer';
import { useSelector } from 'react-redux';
import { telemetry } from '../../../telemetry';
import { ACTIONS_EVENTS } from '../../../services/events';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { ActionsMenu } from '../../common/actions-menu/ActionsMenu';
import { Unplug } from '@sweep-io/sweep-design/dist/icons';

interface HubspotConnectButtonProps {
  variant?: ButtonVariants;
}

enum HubspotConnectionStatus {
  Connected = 'connected',
  NotConnected = 'not connected',
  LoadingData = 'loading data',
}

export const HubspotConnectButton = ({ variant = 'system' }: HubspotConnectButtonProps) => {
  const {
    createHubspotOrg,
    getHubspotOrg,
    hubspotOrgs,
    getHubspotRedirectToOAuthUrl,
    connectedHubspotOrg,
    deleteConnectedHubspotOrg,
    getHubspotOrgs,
  } = useHubspot();
  const { delete_hubspot_org } = useHubspotApiFacade();
  const productionOrg = useSelector(selectProductionCrmOrg);
  const { fetchGlobal } = useFetchGlobal();
  const sendBiEvent = useSendBiEvent();

  const [isConnecting, setIsConnecting] = useState(false);

  //Although it's possible to create multiple hubspotOrgs in the BE,
  // the product is to allow integrating only to 1

  const getHubspotStatus = () => {
    if (!hubspotOrgs) {
      return HubspotConnectionStatus.LoadingData;
    }
    if (connectedHubspotOrg) {
      return HubspotConnectionStatus.Connected;
    } else {
      return HubspotConnectionStatus.NotConnected;
    }
  };
  const hubspotStatus = getHubspotStatus();
  const { evaluatePermissions: checkPermissions } = usePermissions();
  const [isConnectHubspotAllowed] = checkPermissions([{ permission: 'create:hubspot-orgs' }]);

  const verifyThatOrgIsConnected = useCallback(
    async (hubspotOrgId: string) => {
      const getConnectedHubspotOrg = async () => {
        const hsOrg = await getHubspotOrg(hubspotOrgId);
        if (hsOrg?.isConnected) {
          return hsOrg;
        } else {
          return null;
        }
      };
      try {
        // Retries up to 40 seconds until the org returns isConnected
        // Fails if timeout
        //TODO can be replaced with websockets implementation
        await retry(getConnectedHubspotOrg, {
          until: (t) => t !== null,
          timeout: 40000,
          delay: 2000,
        });
      } catch (err) {
        telemetry.captureError(err);
      }
    },
    [getHubspotOrg],
  );

  // This function is used to connect to hubspot
  // It creates a new hubspot org if there are no orgs
  // If there is an org and it's not connected, it deletes the org and creates a new one

  const _connect = useCallback(async () => {
    try {
      const hubspotOrg = await createHubspotOrg();
      return hubspotOrg;
    } catch (error: any) {
      if (error.response.status === 409) {
        const orgs = await getHubspotOrgs();
        if (orgs?.length) {
          const [org] = orgs;
          if (org.isConnected) {
            return org;
          } else {
            await delete_hubspot_org();
            const hubspotOrg = await createHubspotOrg();
            return hubspotOrg;
          }
        }
      }
      throw error;
    }
  }, [createHubspotOrg, delete_hubspot_org, getHubspotOrgs]);

  const connectToHubspot = useCallback(async () => {
    sendBiEvent({ name: ACTIONS_EVENTS.hubspotConnect });
    setIsConnecting(true);
    const hubspotOrg = await _connect();

    if (!hubspotOrg) {
      setIsConnecting(false);
      telemetry.captureError(new Error('hubspotOrg was not created correctly'));
      return;
    }
    const _window = window.open(
      getHubspotRedirectToOAuthUrl({ hubspotOrgId: hubspotOrg.id }),
      '_blank',
    );
    if (_window) {
      await waitForWindowToClose(_window);
      await verifyThatOrgIsConnected(hubspotOrg.id);
    } else {
      telemetry.captureError(new Error('Error connecting to hubspot'));
    }
    setIsConnecting(false);
    if (productionOrg) {
      fetchGlobal({ crmOrgId: productionOrg.id });
    }
  }, [
    _connect,
    fetchGlobal,
    getHubspotRedirectToOAuthUrl,
    productionOrg,
    verifyThatOrgIsConnected,
    sendBiEvent,
  ]);

  return (
    <Box>
      {HubspotConnectionStatus.Connected !== hubspotStatus && (
        <RestrictedTooltip
          validation={{ type: 'explicit', allowed: isConnectHubspotAllowed }}
          notAllowedTitle={'To adjust integration settings, please contact your admin.'}
        >
          <Button
            variant={variant}
            disabled={!isConnectHubspotAllowed}
            loading={hubspotStatus === HubspotConnectionStatus.LoadingData || isConnecting}
            onClick={connectToHubspot}
            size="tiny"
          >
            Connect
          </Button>
        </RestrictedTooltip>
      )}
      {HubspotConnectionStatus.Connected === hubspotStatus && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color={colors.feedback.success}>
            Connected
          </Typography>
          <ActionsMenu
            iconSize="tiny"
            actions={[
              {
                label: 'Disconnect',
                value: 'Disconnect',
                actionIcon: <Unplug color={colors.blush[600]} />,
                disabled: !isConnectHubspotAllowed,
                disabledTooltip: 'To adjust integration settings, please contact your admin.',
                actionSx: { color: colors.blush[600] },
              },
            ]}
            onClick={(action) => {
              if (action?.value === 'Disconnect') {
                deleteConnectedHubspotOrg();
              }
            }}
            iconVariant="system"
            menuWidth="180px"
          />
        </Box>
      )}
    </Box>
  );
};
